import { useMemo, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"

// 30-day daily ribbon used as the background visualization for KPI
// cards on the uptime dashboard. Each cell is one day; the cell's
// colour intensity encodes the day's value (uptime % for the uptime
// card, incident count for the incidents card, drift count for the
// drift card, etc).
//
// We use a ribbon instead of an area / bar sparkline because:
//
//   1. The series we plot are mostly flat (most fleets are mostly up,
//      most days have 0 incidents). An auto-scaled area chart in that
//      regime collapses to an invisible flat line. A discrete cell
//      grid keeps every day visible regardless of distribution.
//   2. Operators read this metaphor instantly -- it's the same
//      pattern used by GitHub's contribution graph, StatusPage, and
//      the per-target `UptimeHistoryBar` lower on the same page.
//   3. The cell-per-day grid makes "today" trivially identifiable
//      (rightmost cell), so the rest of the card's headline number
//      always has a visual anchor.
//
// Positioning is absolute so the ribbon never affects the natural
// flow of the value/sub text above it; the parent card pins it to
// the bottom edge via CSS.

export type RibbonCellVariant =
  | "empty"
  | "up"
  | "partial"
  | "warn"
  | "down"
  | "lo"
  | "mid"
  | "hi"

export type RibbonCell = {
  variant: RibbonCellVariant
  tooltip: string
}

export type UptimeOverviewRibbonProps = ComponentProps<"div"> & {
  cells: RibbonCell[]
}

function UptimeOverviewRibbon(props: UptimeOverviewRibbonProps) {
  const { cells, className, ...divProps } = props

  const hasAny = useMemo(
    () => cells.some((c) => c.variant !== "empty"),
    [cells],
  )
  if (!hasAny) return null

  return (
    <div
      aria-hidden
      className={cn("uptime-overview-card-ribbon", className)}
      {...divProps}
    >
      {cells.map((c) => (
        // Tooltip strings are unique per cell (they include the date),
        // so they double as stable keys without needing an extra id.
        <div
          key={c.tooltip}
          className={cn(
            "uptime-overview-card-ribbon-cell",
            `uptime-overview-card-ribbon-cell-${c.variant}`,
          )}
          title={c.tooltip}
        />
      ))}
    </div>
  )
}

// Maps a fleet-wide daily uptime % to a cell variant. Thresholds
// match the per-target `UptimeHistoryBar` colour ramp so the two
// visualisations agree when an operator scans the page.
//
//   100%       -> up      (full green)
//   >= 99.5%   -> partial (slightly muted green)
//   >= 95%     -> warn    (amber)
//   <  95%     -> down    (red)
//   null       -> empty   (no data that day)
export function uptimePercentToCellVariant(
  pct: number | null,
): RibbonCellVariant {
  if (pct == null) return "empty"
  if (pct >= 100) return "up"
  if (pct >= 99.5) return "partial"
  if (pct >= 95) return "warn"
  return "down"
}

// Maps a daily incident count to one of three intensities so a quiet
// fleet still reads as "tiny dots" rather than vanishing entirely.
// `lo` is intentionally visible so a single 1-incident day still
// shows up in the ribbon.
export function incidentCountToCellVariant(
  count: number | null,
): RibbonCellVariant {
  if (count == null || count === 0) return "empty"
  if (count >= 5) return "hi"
  if (count >= 2) return "mid"
  return "lo"
}

// Build a 30-cell ribbon series from any daily-aggregated array (in
// chronological order, oldest first, length = `days`). Tooltip is
// computed from the date and the value formatter the caller passes
// in.
export function buildRibbonCells({
  series,
  days,
  toVariant,
  formatTooltip,
}: {
  series: Array<number | null>
  days: number
  toVariant: (value: number | null) => RibbonCellVariant
  formatTooltip: (value: number | null, date: string) => string
}): RibbonCell[] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const out: RibbonCell[] = []
  for (let i = 0; i < days; i++) {
    const value = series[i] ?? null
    const date = new Date(today)
    date.setDate(today.getDate() - (days - 1 - i))
    const dateStr = date.toISOString().slice(0, 10)
    out.push({
      variant: toVariant(value),
      tooltip: formatTooltip(value, dateStr),
    })
  }
  return out
}

export default UptimeOverviewRibbon
